import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { useLottie } from 'lottie-react'
import { Check, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingFacts } from '@/components/loading-facts'

const ANIMATION_URL = 'https://assets3.lottiefiles.com/packages/lf20_myejiggj.json'

function LottiePlayer({ animationData }: { animationData: object }) {
  const { View } = useLottie({ animationData, loop: true, autoplay: true })
  return <>{View}</>
}

export default function GoalBuildLoader({
  compact = false,
  goal,
  streamKey,
}: {
  compact?: boolean
  goal?: string
  // Milestones appear live as the plan streams in (see planStream).
  streamKey?: string | null
}) {
  const { t } = useTranslation()
  const live = useQuery(api.planStream.get, streamKey ? { key: streamKey } : 'skip')
  const milestones = live?.milestones ?? []
  const [animationData, setAnimationData] = useState<object | null>(null)

  useEffect(() => {
    fetch(ANIMATION_URL)
      .then((r) => r.json())
      .then(setAnimationData)
      .catch(() => null)
  }, [])

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center flex-1 px-6 text-center',
        compact ? 'py-4' : 'py-16',
      )}
    >
      <div
        className={cn(
          'transition-all duration-500',
          compact || milestones.length ? 'w-40 h-40' : 'w-64 h-64',
        )}
      >
        {animationData ? (
          <LottiePlayer animationData={animationData} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          </div>
        )}
      </div>

      {goal ? (
        <>
          <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('planner.creatingPlan')}
          </p>
          <h2 className="mt-2 max-w-md text-2xl font-black leading-tight tracking-tight text-balance">
            {goal}
          </h2>
        </>
      ) : (
        <h2 className="mt-2 text-2xl font-black tracking-tight">
          {t('planner.buildingPre')}{' '}
          <span className="bg-gradient-to-r from-mind via-[#3DA9FC] to-[#2dd4bf] bg-clip-text text-transparent">
            {t('planner.buildingGradient')}
          </span>
        </h2>
      )}

      {milestones.length ? (
        <ol className="mt-5 flex w-full max-w-sm flex-col gap-2 text-left">
          {milestones.map((m, i) => (
            <li
              key={i}
              className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-card/60 px-3 py-2 animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-text">
                <Check className="size-3" strokeWidth={3} />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{m.title}</span>
              {m.tasks > 0 && (
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {t('planner.streamTasks', { count: m.tasks })}
                </span>
              )}
            </li>
          ))}
          <li className="flex items-center gap-2.5 px-3 py-1 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            {t('planner.streamWriting')}
          </li>
        </ol>
      ) : (
        <LoadingFacts className="mt-5" />
      )}
    </div>
  )
}
